export interface CuratedTransactionDescription {
  headline: string;
  details: string[];
  raw: string;
}

type CuratorRule = {
  regex: RegExp;
  build: (groups: Record<string, string>) => CuratedTransactionDescription;
};

const curatorRules: CuratorRule[] = [
  {
    regex: /^(?<type>Visa Purchase)\s+(?<date>\d{1,2}[A-Za-z]{3})\s+(?<description>.+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, groups.date)
  },
  {
    regex: /^(?<type>Visa Purchase O\/Seas)\s+(?<date>\d{1,2}[A-Za-z]{3})\s+(?<description>.+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, groups.date)
  },
  {
    regex: /^(?<type>Visa Credit)\s+(?<date>\d{1,2}[A-Za-z]{3})\s+(?<description>.+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, groups.date)
  },
  {
    regex: /^(?<type>Internet Deposit)\s+(?<date>\d{1,2}[A-Za-z]{3}\d{2}:\d{2})\s+(?<description>.+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, groups.date)
  },
  {
    regex: /^(?<type>Internet Deposit)\s+(?<description>From\s+\d+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, '')
  },
  {
    regex: /^(?<type>Osko Deposit)\s+(?<date>\d{1,2}[A-Za-z]{3}\d{2}:\d{2})\s+(?<description>.+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, groups.date)
  },
  {
    regex: /^(?<type>Internet Withdrawal)\s+(?<date>\d{1,2}[A-Za-z]{3}\d{2}:\d{2})\s+(?<description>.+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, groups.date)
  },
  {
    regex: /^(?<type>Internet Withdrawal)\s+(?<description>Adjustment)$/i,
    build: (groups) => withMeta(groups.description, groups.type, '')
  },
  {
    regex: /^(?<type>Eftpos Debit)\s+(?<date>\d{1,2}[A-Za-z]{3}\d{2}:\d{2})\s+(?<description>.+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, groups.date)
  },
  {
    regex: /^(?<type>Osko Withdrawal)\s+(?<date>\d{1,2}[A-Za-z]{3}\d{2}:\d{2})\s+(?<description>.+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, groups.date)
  },
  {
    regex: /^(?<type>Osko Withdrawal)\s+(?<left>.+?)\s+(?<date>(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2})\s+(?<right>.+)$/i,
    build: (groups) => withMeta(`${groups.left} ${groups.right}`, groups.type, groups.date)
  },
  {
    regex: /^(?<description>Foreign Currency Conversn Fee)$/i,
    build: (groups) => withMeta(groups.description, groups.description, '')
  },
  {
    regex: /^(?<type>Tpg Internet)\s+(?<description>[A-Za-z0-9-]+)$/i,
    build: (groups) => withMeta(groups.description, groups.type, '')
  },
  {
    regex: /^(?<description>Account-Keeping Fee)$/i,
    build: (groups) => plain(groups.description)
  },
  {
    regex: /^(?<description>Paypal Australia\s+\d+)$/i,
    build: (groups) => plain(groups.description)
  },
  {
    regex: /^(?<description>Red Energy-Ddr\s+\w+)$/i,
    build: (groups) => plain(groups.description)
  }
];

export function curateTransactionDescription(rawValue: string | null | undefined): CuratedTransactionDescription {
  const raw = normalizeWhitespace(rawValue || "");

  if (!raw) {
    return {
      headline: "",
      details: [],
      raw: ""
    };
  }

  for (const rule of curatorRules) {
    const match = rule.regex.exec(raw);
    if (!match?.groups) {
      continue;
    }

    const curated = rule.build(match.groups as Record<string, string>);
    return {
      ...curated,
      raw
    };
  }

  return {
    headline: prettifyDescription(raw),
    details: [],
    raw
  };
}

function withMeta(description: string, type: string, date: string): CuratedTransactionDescription {
  return {
    headline: prettifyDescription(description),
    details: [normalizeType(type), normalizeDate(date)].filter(Boolean),
    raw: ""
  };
}

function plain(description: string): CuratedTransactionDescription {
  return {
    headline: prettifyDescription(description),
    details: [],
    raw: ""
  };
}

function normalizeWhitespace(value: string): string {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeType(value: string): string {
  const normalized = normalizeWhitespace(value).toLowerCase();
  const map: Record<string, string> = {
    "visa purchase": "Card Purchase",
    "visa purchase o/seas": "Card Purchase",
    "visa credit": "Card Credit",
    "internet deposit": "Deposit",
    "osko deposit": "Deposit",
    "internet withdrawal": "Bank Payment",
    "osko withdrawal": "Bank Payment",
    "eftpos debit": "Card Purchase",
    "foreign currency conversn fee": "Currency Conversion",
    "tpg internet": "Bill Payment",
  };

  return map[normalized] || value;
}

function normalizeDate(value: string): string {
  const normalized = normalizeWhitespace(value);

  if (/^\d{1,2}[A-Za-z]{3}$/.test(normalized)) {
    const match = normalized.match(/^(\d{1,2})([A-Za-z]{3})$/);
    return match ? `${match[1]} ${match[2]}` : normalized;
  }

  if (/^\d{1,2}[A-Za-z]{3}\d{2}:\d{2}$/.test(normalized)) {
    const match = normalized.match(/^(\d{1,2})([A-Za-z]{3})(\d{2}:\d{2})$/);
    return match ? `${match[1]} ${match[2]} ${match[3]}` : normalized;
  }

  return normalized;
}

function prettifyDescription(value: string): string {
  return normalizeWhitespace(value)
    .replace(/\bPaypal\b/gi, "PayPal")
    .replace(/\bEbay\b/gi, "eBay")
    .replace(/\bForeign Currency Conversn Fee\b/gi, "Currency Conversion Fee")
    .replace(/\bAccount-Keeping Fee\b/gi, "Bank Fee")
    .replace(/\bConversn\b/gi, "Conversion")
    .replace(/Red Energy-Ddr/gi, "Red Energy DDR");
}
